import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import './label-renderer.scss';
import { FwVersionInfo } from '@src/dtos/version.dto';
import { versionStatusAndStateMap } from '@src/model/version';
import moment from 'moment/moment';
import { SHORT_MONTH_DATE_FORMAT, SHORT_MONTH_DAY_YEAR_FORMAT } from '@src/utils/datesUtils';
import { LevelName, VersionState, VersionStatus } from '@src/utils/enums';
import { ConnectionPolicyData } from '@src/dtos/pull-policy.dto';
import { ServiceDto } from '@src/dtos/customer.dto';
import { DistributePolicyConnection, DistributePolicyState } from '@src/dtos/push-policy.dto';
import InvalidEqual from '@svg/equal-circle.svg?react';
import InvalidPending from '@svg/refresh-small.svg?react';
import PadLock from '@svg/pad-lock-close.svg?react';
import PersonalSecurity from '@svg/personal-security-small.svg?react';
import Wrench from '@svg/wrench.svg?react';
import CalendarClock from '@svg/calendar-clock.svg?react';
import EditIcon from '@svg/edit-pencil.svg?react';
import { Tooltip } from 'antd';
import PolicyStatusDisplay from '../policy-status-display/policy-status-display';
import { ConnectionLevel } from '../connection-level/connection-level';
import { ConnectionType } from '../connection-type/connection-type';

export const getLabel = (versionState?: VersionState, versionStatus?: VersionStatus) =>
    versionState && versionStatus ? versionStatusAndStateMap.get(versionState, versionStatus)?.label : undefined;

export const getPolicyActivationDate = (dateFormat: string, activationTime?: string, versionStatus?: VersionStatus, updatedTimestamp?: string) => {
    if (versionStatus === VersionStatus.DRAFT) {
        return moment.utc(updatedTimestamp).local().format(dateFormat);
    }
    return activationTime ? moment.utc(activationTime).local().format(dateFormat) : '';
};

export const serviceComparatorByName = (serviceA?: ServiceDto, serviceB?: ServiceDto) => {
    const nameA = (serviceA?.serviceName || serviceA?.adiCircuitId || '').toLowerCase();
    const nameB = (serviceB?.serviceName || serviceB?.adiCircuitId || '').toLowerCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
};

export interface ConnectionItemRendererProps {
    data: ServiceDto;
    displayOriginalCircuitId?: boolean;
    allowEdit?: boolean;
    onEditClick?: (e: React.MouseEvent) => void;
    displayCircuitType?: boolean;
}

export const ConnectionItemRenderer: React.FC<ConnectionItemRendererProps> = observer(
    ({ data, displayOriginalCircuitId, allowEdit = false, onEditClick, displayCircuitType = true }) => {
        const { serviceLevel, adiCircuitId, serviceName, circuitType } = data;
        const showCircuitId = displayOriginalCircuitId && serviceName && adiCircuitId && serviceName !== adiCircuitId;

        return (
            <div className={`connection-item ${serviceLevel.toLowerCase()}`}>
                <span className="connection-name">
                    {serviceName || adiCircuitId}
                    {allowEdit && (
                        <button type="button" className="edit-icon" aria-label="Edit" onClick={onEditClick}>
                            <EditIcon />
                        </button>
                    )}
                </span>
                {circuitType && displayCircuitType && (
                    <ConnectionType circuitType={circuitType} />
                )}
                <ConnectionLevel serviceLevel={serviceLevel} />
                {showCircuitId && (
                    <span className="connection-circuit-id">{adiCircuitId}</span>
                )}
            </div>
        );
    });

const distributeStateComponents = {
    [DistributePolicyState.INVALID_EQUAL]: { icon: <InvalidEqual />, title: 'Identical policy in target connection' },
    [DistributePolicyState.INVALID_PENDING]: { icon: <InvalidPending />, title: 'Policy is already being applied in this connection' },
    [DistributePolicyState.INVALID_SMART_POLICY]: { icon: <PersonalSecurity />, title: 'Smart Protection policy is being applied in this connection' },
    [DistributePolicyState.INVALID_MAINTENANCE]: { icon: <Wrench />, title: 'Connection currently in maintenance' },
    [DistributePolicyState.INVALID_SERVICE_LEVEL]: { icon: <PadLock />, title: 'Cannot copy policy to premium connection' },
    [DistributePolicyState.INVALID_SCHEDULED]: { icon: <CalendarClock />, title: 'There is a scheduled policy applied to this connection' },
    [DistributePolicyState.VALID]: undefined
};

export const DistributePolicyConnectionItemRenderer: React.FC<{ data: DistributePolicyConnection }> = observer(({ data }) => {
    const { targetSidLevel, targetSid, targetSidName, distributePolicyState } = data;

    const currentState = useMemo(() => distributeStateComponents[distributePolicyState], [distributePolicyState]);

    return (
        <div className={`connection-item ${targetSidLevel.toLowerCase()}`}>
            <span className="connection-name">{targetSidName || targetSid}</span>
            <div className={`connection-item-info ${distributePolicyState.toLowerCase()}`}>
                {currentState && (
                    <Tooltip title={currentState.title}>
                        {currentState.icon}
                    </Tooltip>
                )}
                <ConnectionLevel serviceLevel={targetSidLevel} />
            </div>
        </div>
    );
});


interface PolicyItemProps {
    data: FwVersionInfo;
    dateFormat?: string;
}

export const PolicyItemRenderer: React.FC<PolicyItemProps> = observer(({ data, dateFormat = SHORT_MONTH_DATE_FORMAT }) => {
    const { versionState, versionStatus, versionNumber, activationTime, versionName, updatedTimestamp } = data;

    const policyActivationDate = useMemo(() =>
        getPolicyActivationDate(dateFormat, activationTime, versionStatus, updatedTimestamp),
        [activationTime, versionStatus, updatedTimestamp, dateFormat]);

    return (
        <div className="policy-item-display">
            <span className="left-display">
                <PolicyStatusDisplay policyNumber={versionNumber} policyStatus={versionStatus} policyState={versionState} />
                <span className="version-name">{versionName}</span>
            </span>
            <span className={`right-display ${policyActivationDate ? 'date' : ''}`}>{policyActivationDate}</span>
        </div>
    );
});

export interface ConnectionAndPolicyItemRendererProps {
    data: ConnectionPolicyData;
    onEditClick?: (connection: ServiceDto, e: React.MouseEvent) => void;
}

export const ConnectionAndPolicyItemRenderer: React.FC<ConnectionAndPolicyItemRendererProps> = observer(({ data, onEditClick }) => {
    const handleEditClick = (e: React.MouseEvent) => {
        if (onEditClick) {
            onEditClick(data.connection as ServiceDto, e);
        }
    };
    return (
        <div className="dropdown-item-display">
            <ConnectionItemRenderer data={data.connection as ServiceDto} allowEdit={true} displayOriginalCircuitId={true} onEditClick={handleEditClick} />
            {data.connection?.serviceLevel !== LevelName.BRONZE ?
                <PolicyItemRenderer data={data.policy}
                    dateFormat={SHORT_MONTH_DAY_YEAR_FORMAT} />
                :
                <span
                    className={'bronze-update-time'}>{moment.utc(data.policy.updatedTimestamp).local().format(SHORT_MONTH_DAY_YEAR_FORMAT)}</span>}
        </div>
    );
});